import { useState } from "react";
import { Autocomplete, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxLabTestDto } from "@/api/diagnostics";

/**
 * Lab test selector, backed by the diagnostics catalog. Replaces the raw "Lab test Id" boxes on the
 * workbench screens — nobody knows a test by its GUID, and a mistyped one fails only after submit.
 *
 * The catalog is small enough to load whole and filter locally, so results are instant and the
 * whole list is browsable when the operator isn't sure of the name.
 */
export function LabTestPicker({
  value, onChange, label = "Lab test", required, disabled, helperText, sx,
}: {
  /** The test id. */
  value: string;
  onChange: (labTestId: string, test?: DxLabTestDto) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  sx?: object;
}) {
  const [input, setInput] = useState("");

  const q = useQuery({
    queryKey: ["lab-tests-picker"],
    queryFn: () => diagnosticsApi.listTests(),
    staleTime: 5 * 60_000,
  });

  const options: DxLabTestDto[] = q.data ?? [];
  const selected = options.find(t => t.id === value) ?? null;

  return (
    <Autocomplete<DxLabTestDto>
      value={selected}
      options={options}
      inputValue={selected && !input ? `${selected.code} — ${selected.name}` : input}
      onInputChange={(_, v, reason) => { if (reason !== "reset") setInput(v); }}
      onChange={(_, v) => { onChange(v?.id ?? "", v ?? undefined); setInput(""); }}
      disabled={disabled || q.isLoading}
      loading={q.isLoading}
      getOptionLabel={(o) => `${o.code} — ${o.name}`}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      size="small"
      sx={sx}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Stack spacing={0}>
            <Typography variant="body2">{o.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {o.code}{o.unit ? ` · ${o.unit}` : ""}{o.isOutsourced ? " · outsourced" : ""}
              {o.isActive ? "" : " · inactive"}
            </Typography>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={`${label}${required ? " *" : ""}`}
          helperText={helperText ?? (q.isError ? "Could not load the test catalog" : undefined)}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isLoading ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }} />
      )}
    />
  );
}
